import { ComponentType, Fragment } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Loader from 'components/loader/Loader';
import {
  GetIntegrationAgencySettingsResponseTO,
  MuSupportType,
} from 'models/InternalIntegrationSetting';
import { PropertiesBodyListStyled } from 'pages/agencyProperties/body/PropertiesBody.styles';
import PropertiesBodyListPlaceholder from 'pages/agencyProperties/body/PropertiesBodyListPlaceholder';
import useSearchFieldFilters from 'pages/agencyProperties/body/useSearchFieldFilters';
import { PropertyForPropertyListItem } from 'pages/agencyProperties/Properties.types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { normalizeKey } from 'utils/localeUtils';
import getSupportedPropertyUids from '../getSupportedPropertyUids';
import useFetchPaginatedProperties from '../useFetchPaginatedProperties';

interface PropertyListItemComponentProps {
  property: PropertyForPropertyListItem;
  isChildProperty?: boolean;
  integrationAgencySettings: GetIntegrationAgencySettingsResponseTO;
  pagePropertyUids: string[];
}

interface PaginatedPropertiesScrollProps {
  integrationAgencySettings: GetIntegrationAgencySettingsResponseTO;
  emptyMessage: string;
  PropertyListItem: ComponentType<PropertyListItemComponentProps>;
  filterProperties?: (
    properties: PropertyForPropertyListItem[],
  ) => PropertyForPropertyListItem[];
}

const PaginatedPropertiesScroll = ({
  integrationAgencySettings,
  emptyMessage,
  PropertyListItem,
  filterProperties,
}: PaginatedPropertiesScrollProps) => {
  const { t } = useTranslation();
  const { searchFieldFilters } = useSearchFieldFilters();
  const status = useWatch({ name: 'status' });
  const tags = useWatch({ name: 'tags' });

  const filters = {
    status,
    tags,
    ...searchFieldFilters,
  };

  const {
    data,
    properties,
    isLoading: isLoadingProperties,
    fetchNextPage,
    hasNextPage,
  } = useFetchPaginatedProperties(filters);

  if (isLoadingProperties) {
    return <PropertiesBodyListPlaceholder />;
  }

  if (!properties.length) {
    return <div>{t(normalizeKey(emptyMessage))}</div>;
  }

  const muSupportType =
    integrationAgencySettings.integration?.muSupportType ?? MuSupportType.NONE;

  return (
    <PropertiesBodyListStyled>
      <InfiniteScroll
        dataLength={properties.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage && properties.length > 0}
        loader={<Loader />}
        scrollableTarget="app-page-content"
      >
        {data?.pages?.map((page, pageIndex) => {
          const pageProperties = filterProperties
            ? filterProperties(page.properties as PropertyForPropertyListItem[])
            : (page.properties as PropertyForPropertyListItem[]);

          const pageUids = getSupportedPropertyUids(
            pageProperties,
            muSupportType,
          );

          return (
            <Fragment key={page.offset ?? pageIndex}>
              {pageProperties.map((property) => (
                <PropertyListItem
                  key={property.uid}
                  property={property}
                  integrationAgencySettings={integrationAgencySettings}
                  pagePropertyUids={pageUids}
                />
              ))}
            </Fragment>
          );
        })}
      </InfiniteScroll>
    </PropertiesBodyListStyled>
  );
};

PaginatedPropertiesScroll.defaultProps = {
  filterProperties: undefined,
};

export default PaginatedPropertiesScroll;
